import { Map as ImmutableMap, Record, type RecordOf } from 'immutable';

import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';

export const FUNDING_ACCOUNTS_REDUCER_KEY = 'fundingAccountStore';

export const FundingAccountParentTypeEnum = Object.freeze({
  BANK: 'BANK',
  INVESTMENT: 'INVESTMENT',
});

export type FundingAccountType =
  | 'CHECKING'
  | 'SAVINGS'
  | 'MONEY_MARKET'
  | 'CASH_MANAGEMENT'
  | 'BROKERAGE'
  | 'IRA'
  | 'KEOGH'
  | 'ROTH_IRA'
  | 'SEP_IRA'
  | 'SIMPLE_IRA'
  | '529'
  | 'UGMA_UTMA'
  | 'OTHER_INVESTMENTS'
  | 'UNKNOWN';
export const FundingAccountTypeEnum = Object.freeze({
  CHECKING: 'CHECKING',
  SAVINGS: 'SAVINGS',
  MONEY_MARKET: 'MONEY_MARKET',
  CASH_MANAGEMENT: 'CASH_MANAGEMENT',
  BROKERAGE: 'BROKERAGE',
  _401K: '401K',
  IRA: 'IRA',
  KEOGH: 'KEOGH',
  ROTH_IRA: 'ROTH_IRA',
  ROTH_401K: 'ROTH_401K',
  SEP_IRA: 'SEP_IRA',
  SIMPLE_IRA: 'SIMPLE_IRA',
  _529: '529',
  UGMA_UTMA: 'UGMA_UTMA',
  _403B: '403B',
  OTHER_INVESTMENTS: 'OTHER_INVESTMENTS',
});

export type AccountOwnerType = 'PERSONAL' | 'JOINT' | 'BUSINESS';
export const FundingAccountOwnerTypeEnum = Object.freeze({
  PERSONAL: 'PERSONAL',
  JOINT: 'JOINT',
  BUSINESS: 'BUSINESS',
});

export type StatusType = 'SUBMITTED' | 'FAILED' | 'VERIFICATION_IN_PROGRESS' | 'UNVERIFIED' | 'DISABLED' | 'VERIFIED';
export const statusEnum = Object.freeze({
  SUBMITTED: 'SUBMITTED',
  FAILED: 'FAILED',
  VERIFICATION_IN_PROGRESS: 'VERIFICATION_IN_PROGRESS',
  UNVERIFIED: 'UNVERIFIED',
  DISABLED: 'DISABLED',
  VERIFIED: 'VERIFIED',
});

export type SubStatusType = 'DEPOSIT_REQUIRED' | 'NO_OP' | 'DEPOSIT_INITIATED' | 'POSTED_VERIFY' | 'DEPOSIT_VERIFY';
export const subStatusEnum = Object.freeze({
  DEPOSIT_REQUIRED: 'DEPOSIT_REQUIRED',
  NO_OP: 'NO_OP',
  DEPOSIT_INITIATED: 'DEPOSIT_INITIATED',
  POSTED_VERIFY: 'POSTED_VERIFY',
  DEPOSIT_VERIFY: 'DEPOSIT_VERIFY',
});

export type FundingAccountProps = {
  id?: string;
  routingNumber?: string;
  accountNumber?: string;
  nickname?: string;
  accountType?: FundingAccountType;
  ownerType?: AccountOwnerType;
  linkedAccountId?: string;
  status?: StatusType;
  subStatus?: SubStatusType;
  verificationId?: string;
  isDeleted?: boolean;
  destinationOnly?: boolean;
  maxTransferAmount?: number;
};

export type FundingAccount = RecordOf<FundingAccountProps>;

export const mkFundingAccount = Record<FundingAccountProps>({
  id: undefined,
  routingNumber: undefined,
  accountNumber: undefined,
  nickname: undefined,
  accountType: undefined,
  ownerType: undefined,
  linkedAccountId: undefined,
  status: undefined,
  subStatus: undefined,
  verificationId: undefined,
  isDeleted: undefined,
  destinationOnly: undefined,
  maxTransferAmount: undefined,
});

// store
export type FundingAccountStoreProps = QcsSyncResourceStoreProps<FundingAccountProps>;
export type FundingAccountStore = QcsSyncResourceStore<FundingAccountProps, FundingAccountStoreProps>;
export const mkFundingAccountStore = Record<FundingAccountStoreProps>({
  resourcesById: ImmutableMap(),

  resourceName: 'fundingAccount',
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,
});

// payloads
export type CreateFundingAccountPayload = {
  routingNumber: string;
  accountNumber: string;
  accountType: FundingAccountType;
  ownerType: AccountOwnerType;
  nickname: string;
  linkedAccountId: string;
};

export type VerifyMicrodepositsPayload = {
  id: string;
  amountOne: string;
  amountTwo: string;
};

export type VerifyMicrodepositsResponse = {
  status: StatusType; // this is not the same status returned in the GET
  subStatus: SubStatusType;
  attempts: number;
  threshold: number;
};
